import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import RocketSpinner from '../Components/Common/RocketSpinner';

interface LoadingState {
  id: string;
  text: string;
  priority: number;
}

interface GlobalLoadingContextType {
  setLoading: (id: string, isLoading: boolean, text?: string, priority?: number) => void;
  isLoading: boolean;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

export const useGlobalLoading = () => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within GlobalLoadingProvider');
  }
  return context;
};

export const GlobalLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState<Map<string, LoadingState>>(new Map());
  const [currentText, setCurrentText] = useState('Lädt...');
  const spinnerTimeoutRef = useRef<NodeJS.Timeout>();
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    // Clear any existing timeout
    if (spinnerTimeoutRef.current) {
      clearTimeout(spinnerTimeoutRef.current);
    }

    if (loadingStates.size > 0) {
      // Get the loading state with highest priority
      let highestPriority: LoadingState | null = null;
      loadingStates.forEach(state => {
        if (!highestPriority || state.priority > highestPriority.priority) {
          highestPriority = state;
        }
      });
      
      if (highestPriority) {
        setCurrentText(highestPriority.text);
      }
      
      // Show spinner immediately if not already showing
      if (!showSpinner) {
        setShowSpinner(true);
      }
    } else {
      // Hide spinner after a short delay to prevent flashing
      spinnerTimeoutRef.current = setTimeout(() => {
        setShowSpinner(false);
      }, 100);
    }

    return () => {
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
      }
    };
  }, [loadingStates, showSpinner]);

  const setLoading = useCallback((id: string, isLoading: boolean, text: string = 'Lädt...', priority: number = 0) => {
    setLoadingStates(prev => {
      const newMap = new Map(prev);
      if (isLoading) {
        newMap.set(id, { id, text, priority });
      } else {
        newMap.delete(id);
      }
      return newMap;
    });
  }, []);

  return (
    <GlobalLoadingContext.Provider value={{ setLoading, isLoading: loadingStates.size > 0 }}>
      {children}
      {showSpinner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--vz-body-bg)',
          zIndex: 99999,
          pointerEvents: 'all'
        }}>
          <RocketSpinner text={currentText} fullScreen={true} />
        </div>
      )}
    </GlobalLoadingContext.Provider>
  );
};

// Helper hook for components
export const useComponentLoading = (id: string, text: string = 'Lädt...', priority: number = 0) => {
  const { setLoading } = useGlobalLoading();
  
  const startLoading = useCallback(() => {
    setLoading(id, true, text, priority);
  }, [id, text, priority, setLoading]);
  
  const stopLoading = useCallback(() => {
    setLoading(id, false);
  }, [id, setLoading]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      setLoading(id, false);
    };
  }, [id, setLoading]);
  
  return { startLoading, stopLoading };
};
